import { communityJsonSource } from '@/container/community/communityJsonSource';
import { communityXmlSource } from '@/container/community/communityXmlSource';
import { useIsJsonFormEnabled, useIsXmlFormEnabled } from '@/features/flags';
import { useMutateFillFormDemoFromUpload } from '@/hooks/mutation/useMutateFillFormDemoFromUpload';
import { useMutateFillXmlFormDemoFromUpload } from '@/hooks/mutation/useMutateFillXmlFormDemoFromUpload';
import { type DetailedPublicTemplateResponse } from '@/types/global-types';
import { useMutateFillJsonFormDemoFromUpload } from '@heidi/templates/data';
import { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { areValidDocumentFiles, isValidAudioFile } from './constants';
import { formFillDemoMessages } from './template-form-demo.messages';
import { type XmlFormFillResult } from './types';

export type UploadFillSource = 'audio' | 'document';

interface UseTemplateFormDemoUploadArgs {
  template: DetailedPublicTemplateResponse;
  onSucceeded: (filledPdfUrl: string) => void;
  onXmlSucceeded: (result: XmlFormFillResult) => void;
  onJsonSucceeded: (fieldValues: Record<string, string>) => void;
  onFailed: (errorMessage: string) => void;
  onSubmit: () => void;
  onFileReady: (file: File, source: UploadFillSource) => void;
  onError: (message: string) => void;
  onStart: () => void;
}

interface TemplateFormDemoUploadSubmitArgs {
  source: UploadFillSource;
  file: File;
}

export interface UseTemplateFormDemoUpload {
  handleAudioFile: (file: File) => void;
  handleDocumentFiles: (files: File[]) => void;
  isPending: boolean;
  pendingSource: UploadFillSource | null;
  submit: (args: TemplateFormDemoUploadSubmitArgs) => void;
}

export function useTemplateFormDemoUpload({
  template,
  onSucceeded,
  onXmlSucceeded,
  onJsonSucceeded,
  onFailed,
  onSubmit,
  onFileReady,
  onError,
  onStart,
}: UseTemplateFormDemoUploadArgs): UseTemplateFormDemoUpload {
  const intl = useIntl();
  const isXmlFormEnabled = useIsXmlFormEnabled();
  const isJsonFormEnabled = useIsJsonFormEnabled();
  const { mutate: fillFormFromUpload, isPending: isPdfPending } =
    useMutateFillFormDemoFromUpload();
  const { mutate: fillXmlFormFromUpload, isPending: isXmlPending } =
    useMutateFillXmlFormDemoFromUpload();
  const { mutate: fillJsonFormFromUpload, isPending: isJsonPending } =
    useMutateFillJsonFormDemoFromUpload();
  const [pendingSource, setPendingSource] = useState<UploadFillSource | null>(null);

  const handleAudioFile = useCallback(
    (file: File) => {
      if (!isValidAudioFile(file)) {
        onError(intl.formatMessage(formFillDemoMessages.errorAudioInvalid));
        return;
      }

      onStart();
      onFileReady(file, 'audio');
    },
    [intl, onError, onFileReady, onStart]
  );

  const handleDocumentFiles = useCallback(
    (files: File[]) => {
      if (!areValidDocumentFiles(files)) {
        onError(intl.formatMessage(formFillDemoMessages.errorDocumentInvalid));
        return;
      }

      const firstFile = files[0];
      if (!firstFile) {
        onError(intl.formatMessage(formFillDemoMessages.errorDocumentInvalid));
        return;
      }

      onStart();
      onFileReady(firstFile, 'document');
    },
    [intl, onError, onFileReady, onStart]
  );

  const submit = useCallback(
    ({ source, file }: TemplateFormDemoUploadSubmitArgs) => {
      const s3ObjectKey = template.template_content.s3_object_key;
      const xmlSource = communityXmlSource(template, isXmlFormEnabled);
      const jsonSource = communityJsonSource(template, isJsonFormEnabled);
      const formFields = template.template_content.form_fields ?? [];
      if ((!s3ObjectKey && !xmlSource && !jsonSource) || formFields.length === 0) {
        return;
      }

      onSubmit();
      setPendingSource(source);

      const handleFillError = (error: unknown) => {
        const isRateLimited = isAxiosError(error) && error.response?.status === 429;
        onFailed(
          intl.formatMessage(
            isRateLimited
              ? formFillDemoMessages.errorRateLimited
              : formFillDemoMessages.errorGeneric
          )
        );
        setPendingSource(null);
      };

      if (jsonSource) {
        fillJsonFormFromUpload(
          {
            source,
            file,
            json_source: jsonSource,
            form_fields: formFields,
            specialty_name: template.specialty,
            language: intl.locale,
          },
          {
            onSuccess: (data) => {
              onJsonSucceeded(data.field_values);
              setPendingSource(null);
            },
            onError: handleFillError,
          }
        );
        return;
      }

      if (xmlSource) {
        fillXmlFormFromUpload(
          {
            source,
            file,
            xml_source: xmlSource,
            form_fields: formFields,
            specialty_name: template.specialty,
            language: intl.locale,
          },
          {
            onSuccess: (data) => {
              onXmlSucceeded({
                filledXml: data.filled_xml,
                fieldValues: data.field_values,
              });
              setPendingSource(null);
            },
            onError: handleFillError,
          }
        );
        return;
      }

      fillFormFromUpload(
        {
          source,
          file,
          s3_object_key: s3ObjectKey ?? '',
          form_fields: formFields,
          specialty_name: template.specialty,
          language: intl.locale,
        },
        {
          onSuccess: (data) => {
            onSucceeded(
              `data:${data.filled_pdf_mime_type};base64,${data.filled_pdf_base64}`
            );
            setPendingSource(null);
          },
          onError: handleFillError,
        }
      );
    },
    [
      template,
      isXmlFormEnabled,
      isJsonFormEnabled,
      onSubmit,
      fillFormFromUpload,
      fillXmlFormFromUpload,
      fillJsonFormFromUpload,
      intl,
      onSucceeded,
      onXmlSucceeded,
      onJsonSucceeded,
      onFailed,
    ]
  );

  return {
    handleAudioFile,
    handleDocumentFiles,
    isPending: isPdfPending || isXmlPending || isJsonPending,
    pendingSource,
    submit,
  };
}
